import React from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@ui/select';
import { useUserCompanies, useCurrentCompany } from '@hooks/useCompany';
import { Loader2, Building2 } from 'lucide-react';

interface CompanySelectorProps {
  className?: string;
}

export const CompanySelector: React.FC<CompanySelectorProps> = ({ className }) => {
  const { companies, isLoading: companiesLoading, switchCompany, isSwitching } = useUserCompanies();
  const { currentCompany, isLoading: currentLoading } = useCurrentCompany();

  // Don't render if user has only one company or no companies
  if (!companiesLoading && (!companies || companies.length <= 1)) {
    return null;
  }

  const handleCompanyChange = (companyId: string) => {
    if (companyId !== currentCompany?.id) {
      switchCompany(companyId);
    }
  };

  if (companiesLoading || currentLoading) {
    return (
      <div className={`flex items-center space-x-2 px-3 py-2 ${className}`}>
        <Loader2 className="h-4 w-4 animate-spin" />
        <span className="text-sm text-muted-foreground">Loading companies...</span>
      </div>
    );
  }

  return (
    <div className={`px-3 py-2 ${className}`}>
      <div className="flex items-center space-x-2 mb-2">
        <Building2 className="h-4 w-4 text-muted-foreground" />
        <span className="text-xs font-medium text-muted-foreground">Active Company</span>
      </div>
      <Select
        value={currentCompany?.id || ''}
        onValueChange={handleCompanyChange}
        disabled={isSwitching}
      >
        <SelectTrigger className="w-full">
          <SelectValue placeholder="Select company" />
        </SelectTrigger>
        <SelectContent>
          {companies?.map(company => (
            <SelectItem key={company.id} value={company.id}>
              {company.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};
